import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, SectionHeading } from "./shared";
import { SENSORS } from "../../data";

function PressureChart() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  // rising line with a sharp spike then drop (rupture)
  const path = "M0,150 L60,148 L120,145 L180,142 L240,135 L300,120 L340,80 L360,20 L365,150 L420,150 L480,150";
  return (
    <div ref={ref} className="relative">
      <svg viewBox="0 0 480 170" className="w-full h-auto">
        {[40, 80, 120].map((y) => (
          <line key={y} x1="0" y1={y} x2="480" y2={y} stroke="rgba(255,255,255,0.06)" />
        ))}
        <line x1="0" y1="46" x2="480" y2="46" stroke="rgba(192,69,58,0.35)" strokeDasharray="4 4" />
        <text x="4" y="42" fill="#c0453a" className="font-mono-data" fontSize="9">46 barg DESIGN</text>
        <motion.path
          d={path}
          fill="none"
          stroke="#b88457"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 2.6, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 6px rgba(184,132,87,0.6))" }}
        />
        <motion.circle
          cx="360"
          cy="20"
          r="4"
          fill="#c0453a"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: [0, 1, 0.4, 1] } : {}}
          transition={{ delay: 2.4, duration: 1.2, repeat: Infinity }}
        />
      </svg>
      <div className="font-mono-data text-[9px] tracking-[0.2em] text-zinc-500 flex justify-between mt-1">
        <span>03:11:38</span>
        <span className="text-[#c0453a]">RUPTURE 03:14:02</span>
        <span>03:14:19</span>
      </div>
    </div>
  );
}

const LOG = [
  "[03:13:50] TIC-401 demand 74% — heater H-401 firing above expected band",
  "[03:13:52] PT-402 47.1 barg :: RATE +0.4 bar/s :: TREND ▲",
  "[03:13:55] ALARM SUPPRESSED :: T-99 within range clamp [305–340]",
  "[03:14:01] PT-402 47.9 barg :: DESIGN 46.0 barg :: EXCEEDED",
  "[03:14:02] LOPC EVENT :: V-402 SHELL :: PSV-402 lift = NULL",
];

export default function Instrumentation() {
  return (
    <section id="instrumentation" className="relative py-28 md:py-40 bg-[#0c161e] border-y border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <SectionHeading
          chapter="04 // INSTRUMENTATION"
          title={<>The data that<br />lied</>}
          kicker="Asritas replays the raw historian for the team. A single miscalibrated thermocouple held its reading inside the alarm band — so the controller kept firing while the vessel climbed toward failure."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Sensor table */}
          <Reveal className="lg:col-span-7">
            <div className="border border-white/10 bg-[#0f1724]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#152430]">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#c0453a]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#b88457]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-600" />
                </div>
                <span className="font-mono-data text-[10px] tracking-[0.2em] text-zinc-500">
                  DCS_HISTORIAN — UNIT4.snapshot
                </span>
              </div>
              <div className="font-mono-data text-[11px] md:text-xs">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 text-zinc-500 border-b border-white/10 tracking-[0.15em]">
                  <div className="col-span-5">TAG</div>
                  <div className="col-span-3">READING</div>
                  <div className="col-span-4 text-right">STATUS</div>
                </div>
                {SENSORS.map((s, i) => (
                  <motion.div
                    key={s.tag}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: i * 0.07, duration: 0.5 }}
                    className={`grid grid-cols-12 gap-2 px-4 py-3.5 border-b border-white/5 items-center ${
                      s.danger ? "bg-[#c0453a]/[0.04]" : ""
                    }`}
                  >
                    <div className="col-span-5 text-zinc-300">{s.tag}</div>
                    <div className={`col-span-3 ${s.danger ? "text-white" : "text-zinc-400"}`}>{s.reading}</div>
                    <div
                      className={`col-span-4 text-right ${
                        s.danger ? "text-[#c0453a]" : "text-[#b88457]"
                      }`}
                    >
                      {s.status}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Chart + log */}
          <div className="lg:col-span-5 space-y-5">
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-[#0f1724] p-5">
                <div className="font-mono-data text-[10px] tracking-[0.2em] text-zinc-500 mb-4">
                  PT-402 · VESSEL PRESSURE (barg)
                </div>
                <PressureChart />
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="border border-white/10 bg-[#0f1724] p-5 font-mono-data text-[10.5px] leading-relaxed">
                {LOG.map((l, i) => (
                  <div
                    key={i}
                    className={i === LOG.length - 1 ? "text-[#c0453a]" : i === 2 ? "text-amber-400/80" : "text-zinc-400"}
                  >
                    {l}
                  </div>
                ))}
                <div className="text-[#b88457] flex items-center gap-1 mt-1">
                  &gt;<span className="cursor-blink">_</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
